/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useRef, useState } from 'react';
import { contactEditorDraft, contactEditorInput, directoryContactInput, saveContactEdit } from '../../contactEditorRecovery';
// This service has no native runtime imports; reuse its durable contract and storage on web.
import { currentContactCreationScope, isCurrentContactCreationScope, readPendingContactCreation, submitContactCreation, resolvePendingContactCreation } from '../../../mobile/navigation/components/vrs/contactCreationRecovery';
import { contactSearchAddKind } from '../../../../../shared/contact-search-add';
import { useStyles } from './ContactsDrawer.styles';
/** A new contact's draft with the search text in its field: a number, an email or a name. */
export function prefilledContactDraft(contact, prefill = '') {
    const draft = contactEditorDraft(contact);
    const text = prefill.trim(), kind = contactSearchAddKind(text);
    if (contact || !text)
        return draft;
    return kind === 'phone' ? { ...draft, phone: text } : kind === 'email' ? { ...draft, email: text }
        : kind === 'name' ? { ...draft, name: text } : draft;
}
export function ContactEditForm({ contact, groups, onCancel, onSaved, onBusyChange, prefill = '' }) {
    const { classes, cx } = useStyles();
    const [draft, setDraft] = useState(() => prefilledContactDraft(contact, prefill));
    const [draftLinkedClientId, setDraftLinkedClientId] = useState();
    const draftRef = useRef(draft);
    const applyDraft = (value) => { draftRef.current = value; setDraft(value); };
    const [saving, setSaving] = useState(false), [ready, setReady] = useState(false);
    const [reviewContact, setReviewContact] = useState(null);
    const [error, setError] = useState(''), [notice, setNotice] = useState('');
    const [pending, setPending] = useState(null);
    const busy = useRef(false), mounted = useRef(true), generation = useRef(0);
    const scope = useRef(null), pendingRef = useRef(null);
    const currentId = useRef(contact?.id);
    currentId.current = contact?.id;
    const readyRef = useRef(false), outcomeVersion = useRef(0);
    const firstField = useRef(null), errorMessage = useRef(null);
    const saveButton = useRef(null), resolveButton = useRef(null);
    const directory = Boolean(contact?.id.startsWith('platform-'));
    const isEdit = Boolean(contact) && !directory, linked = Boolean(contact?.linked_client_id || draftLinkedClientId);
    const directoryDraft = !isEdit && Boolean(directory || draftLinkedClientId);
    const frozen = saving || !ready || Boolean(pending);
    const restorePending = (value) => {
        if (value?.operationId !== pendingRef.current?.operationId)
            setReviewContact(null);
        pendingRef.current = value;
        setPending(value);
        if (value)
            setDraftLinkedClientId(value.input?.kind === 'manual' ? value.input.contact.linkedClientId : undefined);
        if (value?.input?.kind === 'manual') {
            const row = value.input.contact;
            applyDraft({ name: row.name, phone: row.phoneNumber || '', email: row.email || '',
                organization: row.organization || '', notes: row.notes || '', groupIds: row.groupIds || [] });
        }
    };
    useEffect(() => {
        mounted.current = true;
        const version = ++generation.current;
        readyRef.current = false;
        setReady(false);
        setError('');
        setNotice('');
        restorePending(null);
        setDraftLinkedClientId(undefined);
        setReviewContact(null);
        applyDraft(prefilledContactDraft(contact, prefill));
        const initialize = async () => {
            try {
                const account = currentContactCreationScope();
                scope.current = account;
                const earlier = isEdit ? null : await readPendingContactCreation(account);
                if (!mounted.current || generation.current !== version)
                    return;
                if (!isCurrentContactCreationScope(account)) {
                    setError('Your account changed.');
                    return;
                }
                restorePending(earlier);
                readyRef.current = true;
                setReady(true);
            }
            catch (error) {
                if (mounted.current && generation.current === version)
                    setError(error instanceof Error ? error.message : 'The editor could not be opened. Close it and try again.');
            }
        };
        void initialize();
        return () => { mounted.current = false; generation.current++; onBusyChange?.(false); };
    }, [contact?.id]);
    useEffect(() => { if (error)
        errorMessage.current?.focus(); }, [error]);
    useEffect(() => {
        if (!ready)
            return;
        if (pending)
            (pending.input ? saveButton : resolveButton).current?.focus();
        else
            firstField.current?.focus();
    }, [ready, pending?.operationId]);
    const currentView = (version, account, contactId) => mounted.current && version === generation.current && contactId === currentId.current && isCurrentContactCreationScope(account);
    const finishCreation = (result) => {
        if (result.kind === 'manual')
            onSaved(result.contact ? result.entityId : undefined, result.contact ? 'Contact saved.' : 'The earlier save completed, but the contact is no longer active.');
        else
            onSaved(undefined, 'The earlier contact request is confirmed.');
    };
    const handleSave = async () => {
        const account = scope.current, version = generation.current, contactId = contact?.id;
        if (busy.current || renderOutcome !== outcomeVersion.current || !readyRef.current || !account || !mounted.current)
            return;
        if (!currentView(version, account, contactId)) {
            setError('Your account or contact changed.');
            return;
        }
        if (pendingRef.current && !pendingRef.current.input)
            return;
        busy.current = true;
        onBusyChange?.(true);
        setSaving(true);
        setError('');
        setNotice('');
        try {
            if (contact && !directory) {
                const detail = await saveContactEdit(contact, draftRef.current, account);
                if (currentView(version, account, contactId)) {
                    outcomeVersion.current++;
                    onSaved(detail.contact.id, 'Contact updated.', detail);
                }
            }
            else {
                const earlier = pendingRef.current;
                const input = earlier?.input?.kind === 'request'
                    ? { kind: 'request', query: earlier.query || '', message: earlier.input.request.message || '' }
                    : { kind: 'manual', contact: earlier?.input?.kind === 'manual' ? earlier.input.contact : directory && contact ? directoryContactInput(contact) : { ...contactEditorInput(draftRef.current), ...(draftLinkedClientId ? { linkedClientId: draftLinkedClientId } : {}) } };
                const result = await submitContactCreation(account, input, value => {
                    if (currentView(version, account, contactId))
                        restorePending(value);
                }, pendingRef.current?.operationId, () => currentView(version, account, contactId));
                if (!currentView(version, account, contactId))
                    return;
                if (result.reviewRequired && result.creation.kind === 'manual' && result.creation.contact) {
                    setReviewContact(result.creation.contact);
                    setError('The saved contact has different private details.');
                }
                else if (result.cleanupPending) {
                    setError('Save confirmed. Recovery record not cleared.');
                }
                else {
                    outcomeVersion.current++;
                    finishCreation(result.creation);
                }
            }
        }
        catch (error) {
            if (mounted.current && generation.current === version && currentId.current === contactId)
                setError(isCurrentContactCreationScope(account) ? error instanceof Error ? error.message : 'Save not confirmed. Try again.'
                    : 'Your account changed.');
        }
        finally {
            outcomeVersion.current++;
            busy.current = false;
            if (mounted.current) {
                onBusyChange?.(false);
                setSaving(false);
            }
        }
    };
    const handleResolve = async (acceptSavedDetails = false) => {
        const account = scope.current, earlier = pendingRef.current, version = generation.current, contactId = contact?.id;
        if ((acceptSavedDetails && !reviewContact) || busy.current || renderOutcome !== outcomeVersion.current || !readyRef.current || !account || !earlier || !currentView(version, account, contactId))
            return;
        busy.current = true;
        onBusyChange?.(true);
        setSaving(true);
        setError('');
        try {
            const { resolution, cleanupPending, reviewRequired } = await resolvePendingContactCreation(account, earlier, acceptSavedDetails ? reviewContact : null, () => currentView(version, account, contactId));
            if (!currentView(version, account, contactId))
                return;
            if (reviewRequired && resolution.state === 'completed' && resolution.creation.kind === 'manual' && resolution.creation.contact) {
                setReviewContact(resolution.creation.contact);
                setError('The saved contact has different private details.');
                return;
            }
            if (cleanupPending) {
                setError('Outcome confirmed. Recovery record not cleared.');
                return;
            }
            const next = await readPendingContactCreation(account);
            if (!currentView(version, account, contactId))
                return;
            if (next) {
                restorePending(next);
                setNotice('That save is resolved. Another earlier save needs review.');
            }
            else if (resolution.state === 'completed') {
                outcomeVersion.current++;
                finishCreation(resolution.creation);
            }
            else {
                restorePending(null);
                if (directory)
                    applyDraft(contactEditorDraft(contact));
                setNotice('Earlier attempt stopped.');
            }
        }
        catch (error) {
            if (mounted.current && generation.current === version && currentId.current === contactId)
                setError(isCurrentContactCreationScope(account) ? error instanceof Error ? error.message : 'Earlier save not resolved. Try again.'
                    : 'Your account changed.');
        }
        finally {
            outcomeVersion.current++;
            busy.current = false;
            if (mounted.current) {
                onBusyChange?.(false);
                setSaving(false);
            }
        }
    };
    // Guards also cover events retained before React applies the disabled state.
    const renderOutcome = outcomeVersion.current;
    const updateDraft = (patch) => {
        if (directoryDraft || busy.current || pendingRef.current || !readyRef.current || !mounted.current || renderOutcome !== outcomeVersion.current)
            return;
        applyDraft({ ...draftRef.current, ...patch });
    };
    const removeUnlistedGroup = (id) => {
        if (busy.current || pendingRef.current || !readyRef.current || !mounted.current || renderOutcome !== outcomeVersion.current || groups.some(group => group.id === id))
            return;
        applyDraft({ ...draftRef.current, groupIds: draftRef.current.groupIds.filter(value => value !== id) });
    };
    const fields = [
        { key: 'name', label: 'Name', type: 'text', maxLength: 100 },
        { key: 'phone', label: 'Phone', type: 'tel', maxLength: 60 },
        { key: 'email', label: 'Email', type: 'email', maxLength: 254 },
        { key: 'organization', label: 'Organization', type: 'text', maxLength: 1000 }
    ];
    const unlistedGroups = draft.groupIds.filter(id => !groups.some(group => group.id === id));
    return (React.createElement("form", { className: classes.editForm, "aria-busy": saving || (!ready && !error), noValidate: true, onSubmit: event => { event.preventDefault(); return handleSave(); } },
        React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
            React.createElement("span", { className: classes.headerTitle }, pending ? 'Recover earlier save' : directory ? 'Save Contact' : isEdit ? 'Edit Contact' : 'New Contact'),
            React.createElement("button", { className: classes.headerBtn, disabled: saving, style: { minHeight: 44 }, type: 'button', onClick: () => { if (!busy.current && mounted.current)
                    onCancel(); } }, pending ? 'Keep for later' : 'Cancel')),
        !ready && !error && React.createElement("div", { role: 'status' }, "Checking earlier saves\u2026"),
        error && React.createElement("div", { ref: errorMessage, tabIndex: -1, role: 'alert', style: { marginBottom: 12, overflowWrap: 'anywhere' } }, error),
        notice && React.createElement("div", { role: 'status' }, notice),
        reviewContact && React.createElement("section", { "aria-label": 'Saved contact details', style: { overflowWrap: 'anywhere' } },
            React.createElement("p", null,
                React.createElement("strong", null, "Saved details")),
            React.createElement("p", null,
                "Name: ",
                reviewContact.name),
            React.createElement("p", null,
                "Organization: ",
                reviewContact.organization || 'None'),
            React.createElement("p", { style: { whiteSpace: 'pre-wrap' } },
                "Description: ",
                reviewContact.notes || 'None'),
            React.createElement("p", null,
                "Favorite: ",
                reviewContact.isFavorite ? 'Yes' : 'No'),
            React.createElement("p", null,
                "Groups: ",
                reviewContact.groupNames.join(', ') || 'None'),
            React.createElement("button", { className: classes.saveBtn, type: 'button', disabled: saving || !ready, style: { minHeight: 44 }, onClick: () => handleResolve(true) }, "Use saved details")),
        pending?.input?.kind === 'request' ? React.createElement("p", null,
            "Earlier contact request: ",
            pending.query || pending.input.request.recipientClientId) : fields.filter(field => !directory || pending || field.key !== 'organization').map(field => (React.createElement("div", { className: classes.formField, key: field.key },
            React.createElement("label", { className: classes.formLabel },
                field.label,
                field.key === 'name' ? ' *' : '',
                React.createElement("input", { ref: field.key === (linked && !directory ? 'organization' : 'name') ? firstField : undefined, "aria-label": field.label, className: classes.formInput, value: draft[field.key], disabled: frozen, readOnly: directoryDraft || (linked && field.key !== 'organization'), maxLength: field.maxLength, style: { display: 'block', width: '100%', marginTop: 4 }, onChange: event => updateDraft({ [field.key]: event.target.value }), type: field.type }))))),
        (!directory || pending) && React.createElement("div", { className: classes.formField },
            React.createElement("label", { className: classes.formLabel },
                "Description",
                React.createElement("textarea", { "aria-label": 'Description', className: classes.formInput, value: draft.notes, rows: 3, disabled: frozen, readOnly: directoryDraft, maxLength: 1000, style: { display: 'block', width: '100%', marginTop: 4 }, onChange: event => updateDraft({ notes: event.target.value }) }))),
        (!directory || pending) && (groups.length > 0 || unlistedGroups.length > 0) && React.createElement("fieldset", { style: { border: 0, padding: 0, margin: '0 0 12px' } },
            React.createElement("legend", { className: classes.formLabel }, "Groups"),
            React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
                groups.map(group => React.createElement("button", { key: group.id, type: 'button', disabled: frozen || directoryDraft, className: cx(classes.groupChip, draft.groupIds.includes(group.id) && classes.groupChipActive), "aria-pressed": draft.groupIds.includes(group.id), style: { minHeight: 44, maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere' }, onClick: () => updateDraft({ groupIds: draftRef.current.groupIds.includes(group.id) ? draftRef.current.groupIds.filter(id => id !== group.id) : [...draftRef.current.groupIds, group.id] }) }, group.name)),
                unlistedGroups.map(id => React.createElement("button", { key: id, type: 'button', disabled: frozen, className: classes.headerBtn, style: { minHeight: 44 }, onClick: () => removeUnlistedGroup(id) }, `Remove ${contact?.groups?.find(group => group.id === id)?.name || 'unlisted group'}`)))),
        pending && React.createElement("button", { ref: resolveButton, className: classes.headerBtn, disabled: saving || !ready, style: { minHeight: 44 }, type: 'button', onClick: () => handleResolve(false) }, "Resolve earlier save"),
        React.createElement("button", { ref: saveButton, className: classes.saveBtn, style: { minHeight: 44 }, disabled: saving || !ready || Boolean(pending && !pending.input) || (!pending && !draft.name.trim()), type: 'submit' }, saving ? 'Saving…' : pending ? 'Retry original save' : directory ? 'Save Contact' : isEdit ? 'Update Contact' : 'Add Contact')));
}
